import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddRounded,
  ArticleOutlined,
  DeleteOutlineRounded,
  EditOutlined,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import { EditorNewsItem, NewsStatus } from "../../../Models/ambassador";
import { useDeleteNewsItem, useEditorNews } from "../../../Query/useAmbassador";
import AmbassadorConfirmDialog from "../Common/AmbassadorConfirmDialog";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
  StatusChip,
} from "../Common/AmbassadorUi";
import { formatDateTime, parseDate } from "../Common/format";
import {
  NEWS_STATUSES,
  useAmbassadorLabels,
} from "../Common/useAmbassadorLabels";
import NewsEditorDialog from "./NewsEditorDialog";

const PAGE_SIZE = 15;

/** News editing for como: drafts and published items. */
const NewsManagement: React.FC = () => {
  const { t, i18n } = useTranslation("ambassador");
  const labels = useAmbassadorLabels();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<NewsStatus | null>(null);
  // `null` creates a new item, an item edits it, `undefined` is closed.
  const [editing, setEditing] = useState<EditorNewsItem | null | undefined>(
    undefined,
  );
  const [deleting, setDeleting] = useState<EditorNewsItem | null>(null);
  const news = useEditorNews({ page, page_size: PAGE_SIZE, status });
  const deleteNews = useDeleteNewsItem();
  const pageCount = Math.ceil((news.data?.total ?? 0) / PAGE_SIZE);

  const statusChip = (item: EditorNewsItem) => {
    if (item.status === NewsStatus.DRAFT) {
      return <StatusChip label={labels.newsStatus(item.status)} tone="gray" />;
    }
    const scheduled =
      item.published_at && parseDate(item.published_at) > new Date();
    return scheduled ? (
      <StatusChip label={t("newsManagement.scheduled")} tone="orange" />
    ) : (
      <StatusChip label={labels.newsStatus(item.status)} tone="green" />
    );
  };

  return (
    <>
      <PageHeader
        title={t("newsManagement.title")}
        subtitle={t("newsManagement.subtitle")}
        actions={
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={() => setEditing(null)}
          >
            {t("newsManagement.create")}
          </Button>
        }
      />

      <Panel
        title={t("newsManagement.listTitle")}
        action={
          <TextField
            select
            size="small"
            label={t("newsManagement.fields.status")}
            value={status ?? ""}
            onChange={(event) => {
              setStatus(
                event.target.value === "" ? null : Number(event.target.value),
              );
              setPage(1);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">{t("common.all")}</MenuItem>
            {NEWS_STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {labels.newsStatus(value)}
              </MenuItem>
            ))}
          </TextField>
        }
      >
        {news.isLoading ? (
          <LoadingState />
        ) : news.isError ? (
          <ErrorState onRetry={() => news.refetch()} />
        ) : !news.data?.news.length ? (
          <EmptyState
            icon={<ArticleOutlined />}
            title={t("newsManagement.emptyTitle")}
            text={t("newsManagement.emptyText")}
          />
        ) : (
          <>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("newsManagement.fields.title")}</TableCell>
                    <TableCell>{t("newsManagement.fields.category")}</TableCell>
                    <TableCell>{t("newsManagement.fields.status")}</TableCell>
                    <TableCell>
                      {t("newsManagement.fields.publishedAt")}
                    </TableCell>
                    <TableCell>{t("newsManagement.lastChange")}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {news.data.news.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                          {item.title}
                        </Typography>
                        {item.teaser && (
                          <Typography
                            sx={{ fontSize: 12, color: color.gray500 }}
                          >
                            {item.teaser}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {labels.newsCategory(item.category)}
                      </TableCell>
                      <TableCell>{statusChip(item)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatDateTime(item.published_at, i18n.language) ||
                          "–"}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatDateTime(item.updated_at, i18n.language)}
                        {item.updated_by && (
                          <Typography
                            sx={{ fontSize: 12, color: color.gray500 }}
                          >
                            {item.updated_by}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        <Tooltip title={t("common.edit")}>
                          <IconButton
                            size="small"
                            onClick={() => setEditing(item)}
                          >
                            <EditOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("common.delete")}>
                          <IconButton
                            size="small"
                            onClick={() => setDeleting(item)}
                          >
                            <DeleteOutlineRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            {pageCount > 1 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  size="small"
                />
              </Box>
            )}
          </>
        )}
      </Panel>

      {editing !== undefined && (
        <NewsEditorDialog
          item={editing}
          onClose={() => setEditing(undefined)}
        />
      )}
      <AmbassadorConfirmDialog
        open={deleting !== null}
        title={t("newsManagement.deleteTitle")}
        text={t("newsManagement.deleteText", { title: deleting?.title ?? "" })}
        confirmLabel={t("common.delete")}
        danger
        pending={deleteNews.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={() =>
          deleting &&
          deleteNews.mutate(deleting.id, { onSuccess: () => setDeleting(null) })
        }
      />
    </>
  );
};

export default NewsManagement;
